import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useHistory, useParams, useLocation, Redirect } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
	faArrowLeft,
	faSync,
	faCheck,
	faTimesCircle,
} from "@fortawesome/free-solid-svg-icons"
import {
	postEntite,
	postEntiteReset,
	deleteEntite,
	deleteEntiteReset,
	deleteClientAdresse,
} from "../../../store/actions/entiteActions"
import { fetchAutreEntites } from "../../../store/actions/autreActions"
import { URL } from "../../../store/actions/depannageActions"
import { getBaseAdresse } from "../../../utilities/defaultObjects"
import { ENTITE_TYPE_SOCIETE } from "../entite/Entite"
import { PERSONNE_APPELLATION } from "../entite/Personne"
import { COLORS } from "../../../utilities/Constants"
import { generalStyles as gs } from "../../styles/styles"
import Loader from "../../basics/Loader"
import CustomModal from "../../basics/interface/CustomModal"
import EntiteAdressesV2 from "../entite/EntiteAdressesV2"
import MainOeuvreRabaisV2 from "../entite/MainOeuvreRabaisV2"
import ClientVehiculesV2 from "./ClientVehiculesV2"
import ClientToRdvModalV2 from "./ClientToRdvModalV2"
import ClientToAtelierModalV2 from "./ClientToAtelierModalV2"
import ClientToLocationModalV2 from "./ClientToLocationModalV2"
import useQuitGuard from "../../../hooks/useQuitGuard"
import {
	CLIENT_TYPES,
	clientInvalide,
	clientVersFormData,
	nouveauClient,
} from "./clientCreation"
import { useRights } from "../../../hooks/rights"

// Le VehiculeSerializer partagé renvoie encore les dates en "DD-MM-YYYY" ; côté
// fiche V2 on travaille en ISO (input type=date) → normalisation à l'ingestion.
const frToIso = (d) => {
	if (!d || typeof d !== "string") return d || ""
	const p = d.split("-")
	return p.length === 3 && p[0].length === 2 ? `${p[2]}-${p[1]}-${p[0]}` : d
}
const vehiculesToIso = (vehs) =>
	(vehs || []).map((v) => ({
		...v,
		date_mise_en_circu: frToIso(v.date_mise_en_circu),
		expertise_le: frToIso(v.expertise_le),
	}))

const styles = {
	header: {
		display: "flex",
		alignItems: "center",
		gap: 16,
		marginBottom: 22,
	},
	back: {
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		width: 38,
		height: 38,
		borderRadius: 10,
		border: `1px solid ${COLORS.border}`,
		backgroundColor: COLORS.surface,
		color: COLORS.text,
		cursor: "pointer",
		flexShrink: 0,
	},
	titleWrap: { flex: 1, minWidth: 0 },
	kicker: {
		fontSize: 12,
		fontWeight: 600,
		letterSpacing: "0.06em",
		textTransform: "uppercase",
		color: COLORS.textLight,
	},
	name: {
		fontSize: 22,
		fontWeight: 600,
		letterSpacing: "-0.01em",
		color: COLORS.text,
		marginTop: 2,
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
	},
	actions: { display: "flex", gap: 10, flexShrink: 0 },
	createRow: {
		display: "flex",
		justifyContent: "center",
		gap: 10,
		flexWrap: "wrap",
		marginBottom: 18,
	},
	btnDanger: (busy) => ({
		padding: "10px 18px",
		borderRadius: 10,
		border: `1px solid ${COLORS.danger}`,
		backgroundColor: COLORS.dangerLight,
		color: COLORS.danger,
		fontSize: 13,
		fontWeight: 500,
		fontFamily: "inherit",
		cursor: busy ? "not-allowed" : "pointer",
		opacity: busy ? 0.5 : 1,
	}),
	flagRow: {
		display: "inline-flex",
		alignItems: "center",
		gap: 8,
		cursor: "pointer",
		fontSize: 13,
		color: COLORS.textMuted,
	},
	subGroupLabel: {
		fontSize: 11,
		fontWeight: 600,
		color: COLORS.textLight,
		letterSpacing: "0.08em",
		textTransform: "uppercase",
		marginTop: 18,
		marginBottom: 10,
	},
}

const req = <span style={{ color: COLORS.danger }}> *</span>

const ClientFicheV2 = () => {
	const { id } = useParams()
	// Droits « champ » de la matrice : les deux cases de paiement ne sont pas
	// ouvertes à tout le monde (miroir serveur dans EntiteSaveView/EntiteView —
	// hors droit, la valeur postée est ignorée).
	const { hasRight } = useRights()
	const peutMauvaisPayeur = hasRight("client_mauvais_payeur")
	const peutSurFacture = hasRight("client_sur_facture")
	const location = useLocation()
	const history = useHistory()
	const dispatch = useDispatch()
	const { token } = useSelector((s) => s.navInfo.login)
	const postState = useSelector((s) => s.entiteInfo.postEntite)
	const deleteState = useSelector((s) => s.entiteInfo.deleteEntite)
	const isCreate = id === "nouveau"

	const [entite, setEntite] = useState(null)
	const [loading, setLoading] = useState(!isCreate)
	const [denied, setDenied] = useState(false) // type non autorisé → retour liste
	const [showSaveModal, setShowSaveModal] = useState(false)
	const [showDeleteModal, setShowDeleteModal] = useState(false)
	const [showRdvModal, setShowRdvModal] = useState(false)
	const [showAtelierModal, setShowAtelierModal] = useState(false)
	const [showLocationModal, setShowLocationModal] = useState(false)
	const [adresseToDelete, setAdresseToDelete] = useState(null) // index | null
	const [deletingAdresse, setDeletingAdresse] = useState(false)
	const { setClean, setDirty, QuitGuard } = useQuitGuard()

	// Chargement : création (type via ?type=) ou édition (fetch par id).
	useEffect(() => {
		if (isCreate) {
			const q = new URLSearchParams(location.search)
			setEntite(nouveauClient(parseInt(q.get("type"))))
			return
		}
		// Dépend de `id` : après création, la redirection vers /clients/:newId ne
		// remonte pas le composant (même Route v5) → on recharge ici.
		setLoading(true)
		fetch(`${URL}api/entites?id=${id}`, {
			headers: { Authorization: `Bearer ${token}` },
		})
			.then((res) => res.json())
			.then((json) => {
				// Garde-fou : on n'ouvre que société / personne physique.
				if (!json || !CLIENT_TYPES.includes(json.type)) {
					setDenied(true)
					return
				}
				setEntite({
					...json,
					vehicules: vehiculesToIso(json.vehicules),
				})
				setLoading(false)
			})
			.catch(() => setDenied(true))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id])

	useEffect(
		() => () => {
			dispatch(postEntiteReset())
			dispatch(deleteEntiteReset())
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	)

	// Après suppression → retour liste.
	useEffect(() => {
		if (!deleteState.deleted) return
		setClean()
		const t = setTimeout(() => history.push("/clients"), 0)
		return () => clearTimeout(t)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [deleteState.deleted])

	if (denied) return <Redirect to="/clients" />
	if (loading || !entite)
		return (
			<div style={gs.page}>
				<Loader variant="ring" message="Chargement du client…" />
			</div>
		)

	const isSociete = entite.type === ENTITE_TYPE_SOCIETE
	const update = (key, value) => {
		setDirty()
		setEntite((e) => ({ ...e, [key]: value }))
	}
	const updateExtra = (key, value) => {
		setDirty()
		setEntite((e) => ({
			...e,
			extra_data: { ...(e.extra_data || {}), [key]: value },
		}))
	}
	const updateAdresse = (index, key, value, whole = false) => {
		setDirty()
		setEntite((e) => ({
			...e,
			adresses: e.adresses.map((a, i) =>
				i !== index ? a : whole ? value : { ...a, [key]: value },
			),
		}))
	}
	const addAdresse = () => {
		setDirty()
		setEntite((e) => ({
			...e,
			adresses: [...(e.adresses || []), getBaseAdresse()],
		}))
	}
	// Le bouton corbeille ouvre le modal ; la suppression réelle est confirmée.
	const removeAdresse = (index) => setAdresseToDelete(index)
	const confirmRemoveAdresse = () => {
		const index = adresseToDelete
		const adr = entite.adresses[index]
		// Pas encore sauvegardée → simple retrait de la liste.
		if (!adr || !adr.id) {
			setDirty()
			setEntite((e) => ({
				...e,
				adresses: e.adresses.filter((_, i) => i !== index),
			}))
			setAdresseToDelete(null)
			return
		}
		// Sauvegardée → suppression serveur, puis on rafraîchit les adresses
		// depuis la réponse (sans écraser les autres champs en cours d'édition).
		setDeletingAdresse(true)
		dispatch(deleteClientAdresse(entite.id, adr.id)).then((updated) => {
			if (updated)
				setEntite((e) => ({ ...e, adresses: updated.adresses }))
			setDeletingAdresse(false)
			setAdresseToDelete(null)
		})
	}
	const toggleMauvaisPayeur = () => {
		setDirty()
		setEntite((e) => {
			const next = !e.is_mauvais_payeur
			return {
				...e,
				is_mauvais_payeur: next,
				...(next ? { can_payer_par_facture: false } : {}),
			}
		})
	}

	const buildFormData = () => clientVersFormData(entite)

	const save = () =>
		dispatch(postEntite(buildFormData())).then((saved) => {
			if (!saved || !saved.id) return // échec affiché dans le modal
			dispatch(fetchAutreEntites())
			dispatch(postEntiteReset())
			setClean()
			setShowSaveModal(false)
			if (isCreate) {
				// On reste sur la fiche du nouveau client (mode édition) pour
				// enchaîner sur les véhicules. setTimeout(0) : laisser canQuit se
				// propager avant de naviguer (sinon le Prompt du guard bloque).
				setTimeout(() => history.replace(`/clients/${saved.id}`), 0)
			} else {
				// Édition : resync du state local avec la réponse serveur (ids
				// des nouvelles adresses/personnes, champs normalisés). Même
				// forme que le chargement → on convertit les dates véhicules.
				setEntite({
					...saved,
					vehicules: vehiculesToIso(saved.vehicules),
				})
			}
		})

	const confirmDeleteClient = () => dispatch(deleteEntite({ id: entite.id }))

	// Fermeture des modaux de confirmation : on purge aussi l'erreur du slice
	// pour ne pas la retrouver à la prochaine ouverture.
	const closeSaveModal = () => {
		dispatch(postEntiteReset())
		setShowSaveModal(false)
	}
	const closeDeleteModal = () => {
		dispatch(deleteEntiteReset())
		setShowDeleteModal(false)
	}

	const modalError = (error) => (
		<p
			style={{
				margin: "10px 0 0",
				fontSize: 13,
				color: COLORS.danger,
				textAlign: "center",
			}}
		>
			<FontAwesomeIcon icon={faTimesCircle} />{" "}
			{(error && error.message) || "Une erreur est survenue."}
		</p>
	)

	const invalid = clientInvalide(entite)

	const busy =
		postState.saving ||
		postState.saved ||
		deleteState.deleting ||
		deleteState.deleted

	const displayName = isSociete
		? entite.label || "Nouvelle société"
		: `${entite.prenom || ""} ${entite.nom || ""}`.trim() ||
			"Nouvelle personne"

	return (
		<div style={gs.page}>
			<div style={{ width: "100%", maxWidth: 720, margin: "0 auto" }}>
				<div style={styles.header}>
					<button
						style={styles.back}
						onClick={() => history.push("/clients")}
						title="Retour à la liste"
					>
						<FontAwesomeIcon icon={faArrowLeft} />
					</button>
					<div style={styles.titleWrap}>
						<div style={styles.kicker}>
							{isSociete ? "Société" : "Personne physique"}
						</div>
						<div style={styles.name}>{displayName}</div>
					</div>
					<div style={styles.actions}>
						{!isCreate && (
							<button
								style={styles.btnDanger(busy)}
								onClick={() => setShowDeleteModal(true)}
								disabled={busy}
							>
								Supprimer
							</button>
						)}
						<button
							style={{
								...gs.btnPrimary,
								...((busy || invalid) && {
									opacity: 0.5,
									cursor: "not-allowed",
								}),
							}}
							onClick={() => setShowSaveModal(true)}
							disabled={busy || invalid}
						>
							Enregistrer
						</button>
					</div>
				</div>

				{/* ===== Actions « créer depuis ce client » ===== */}
				{(() => {
					const hasId = entite.id != null
					if (!hasId) return null
					const savedAdresses = (entite.adresses || []).filter(
						(a) => a.id != null,
					)
					const savedVehicules = (entite.vehicules || []).filter(
						(v) => v.id != null,
					)
					const canLocation = savedAdresses.length > 0
					const canAtelier =
						savedAdresses.length > 0 && savedVehicules.length > 0
					return (
						<div style={styles.createRow}>
							<button
								style={gs.btnSecondary}
								onClick={() => setShowRdvModal(true)}
							>
								+ Rendez-vous
							</button>
							{canAtelier && (
								<button
									style={gs.btnSecondary}
									onClick={() => setShowAtelierModal(true)}
								>
									+ Fiche atelier
								</button>
							)}
							{canLocation && (
								<button
									style={gs.btnSecondary}
									onClick={() => setShowLocationModal(true)}
								>
									+ Fiche location
								</button>
							)}
						</div>
					)
				})()}

				{/* ===== Identité ===== */}
				<div style={gs.card}>
					<div style={gs.cardTitle}>Identité</div>
					<div style={gs.fieldGroup}>
						{isSociete ? (
							<>
								<div style={gs.fieldRow}>
									<label style={gs.label}>Label{req}</label>
									<input
										style={gs.input}
										value={entite.label || ""}
										onChange={(e) =>
											update("label", e.target.value)
										}
									/>
								</div>
								<div style={gs.fieldRow}>
									<label style={gs.label}>
										Téléphone{req}
									</label>
									<input
										style={gs.input}
										value={entite.telephone || ""}
										onChange={(e) =>
											update("telephone", e.target.value)
										}
									/>
								</div>
								<div style={gs.fieldRow}>
									<label style={gs.label}>
										Tél. supplémentaire
									</label>
									<input
										style={gs.input}
										value={
											(entite.extra_data &&
												entite.extra_data.telephone2) ||
											""
										}
										onChange={(e) =>
											updateExtra(
												"telephone2",
												e.target.value,
											)
										}
									/>
								</div>
								<div style={gs.fieldRow}>
									<label style={gs.label}>E-mail</label>
									<input
										style={gs.input}
										value={entite.email || ""}
										onChange={(e) =>
											update("email", e.target.value)
										}
									/>
								</div>
							</>
						) : (
							<>
								<div style={gs.fieldRow}>
									<label style={gs.label}>Appellation</label>
									<select
										style={gs.input}
										value={entite.appellation ?? 0}
										onChange={(e) =>
											update(
												"appellation",
												parseInt(e.target.value),
											)
										}
									>
										{PERSONNE_APPELLATION.map((a) => (
											<option key={a[0]} value={a[0]}>
												{a[1]}
											</option>
										))}
									</select>
								</div>
								<div style={gs.fieldRow}>
									<label style={gs.label}>Nom{req}</label>
									<input
										style={gs.input}
										value={entite.nom || ""}
										onChange={(e) =>
											update("nom", e.target.value)
										}
									/>
								</div>
								<div style={gs.fieldRow}>
									<label style={gs.label}>Prénom{req}</label>
									<input
										style={gs.input}
										value={entite.prenom || ""}
										onChange={(e) =>
											update("prenom", e.target.value)
										}
									/>
								</div>
								<div style={gs.fieldRow}>
									<label style={gs.label}>
										Date de naissance
									</label>
									<input
										type="date"
										style={gs.input}
										value={entite.date_naissance || ""}
										onChange={(e) =>
											update(
												"date_naissance",
												e.target.value,
											)
										}
									/>
								</div>
								<div style={gs.fieldRow}>
									<label style={gs.label}>
										Téléphone{req}
									</label>
									<input
										style={gs.input}
										value={entite.telephone || ""}
										onChange={(e) =>
											update("telephone", e.target.value)
										}
									/>
								</div>
								<div style={gs.fieldRow}>
									<label style={gs.label}>E-mail</label>
									<input
										style={gs.input}
										value={entite.email || ""}
										onChange={(e) =>
											update("email", e.target.value)
										}
									/>
								</div>

								<div style={styles.subGroupLabel}>Permis</div>
								<div style={gs.fieldRow}>
									<label style={gs.label}>N° de permis</label>
									<input
										style={gs.input}
										value={entite.numero_permis || ""}
										onChange={(e) =>
											update(
												"numero_permis",
												e.target.value,
											)
										}
									/>
								</div>
								<div style={gs.fieldRow}>
									<label style={gs.label}>
										Date d'obtention
									</label>
									<input
										type="date"
										style={gs.input}
										value={
											entite.date_obtention_permis || ""
										}
										onChange={(e) =>
											update(
												"date_obtention_permis",
												e.target.value,
											)
										}
									/>
								</div>
								<div style={gs.fieldRow}>
									<label style={gs.label}>Catégorie</label>
									<input
										style={gs.input}
										value={entite.permis || ""}
										onChange={(e) =>
											update("permis", e.target.value)
										}
									/>
								</div>
							</>
						)}

						{/* ===== Main d'œuvre ===== */}
						<div style={styles.subGroupLabel}>Main d'œuvre</div>
						<MainOeuvreRabaisV2
							type={entite.type}
							extraData={entite.extra_data}
							updateExtra={updateExtra}
						/>

						{/* ===== Paiement ===== */}
						<div style={styles.subGroupLabel}>Paiement</div>
						<div style={gs.fieldRow}>
							<label style={gs.label}>Mauvais payeur</label>
							<label style={styles.flagRow}>
								<input
									type="checkbox"
									disabled={!peutMauvaisPayeur}
									checked={!!entite.is_mauvais_payeur}
									onChange={toggleMauvaisPayeur}
								/>
								{entite.is_mauvais_payeur ? "Oui" : "Non"}
							</label>
						</div>
						<div style={gs.fieldRow}>
							<label style={gs.label}>Paiement par facture</label>
							<label
								style={{
									...styles.flagRow,
									...(entite.is_mauvais_payeur && {
										opacity: 0.5,
										cursor: "not-allowed",
									}),
								}}
							>
								<input
									type="checkbox"
									disabled={
										entite.is_mauvais_payeur ||
										!peutSurFacture
									}
									checked={!!entite.can_payer_par_facture}
									onChange={(e) =>
										update(
											"can_payer_par_facture",
											e.target.checked,
										)
									}
								/>
								{entite.can_payer_par_facture
									? "Autorisé"
									: "Non"}
							</label>
						</div>
					</div>
				</div>

				{/* ===== Adresses ===== */}
				<div style={gs.mt18}>
					<EntiteAdressesV2
						adresses={entite.adresses}
						updateAdresse={updateAdresse}
						addAdresse={addAdresse}
						deleteAdresse={removeAdresse}
					/>
				</div>

				{/* ===== Véhicules ===== */}
				<div style={gs.mt18}>
					<ClientVehiculesV2
						entiteId={entite.id}
						vehicules={entite.vehicules}
						isCreate={isCreate}
						onClientUpdate={(client) =>
							setEntite((e) => ({
								...e,
								vehicules: vehiculesToIso(client.vehicules),
							}))
						}
						onVehiculeDocsChange={(vehiculeId, docs) =>
							setEntite((e) => ({
								...e,
								vehicules: (e.vehicules || []).map((v) =>
									v.id === vehiculeId
										? {
												...v,
												files: docs.files,
												carte_grise: docs.carte_grise,
											}
										: v,
								),
							}))
						}
					/>
				</div>
			</div>

			{showSaveModal && (
				<CustomModal
					title="Enregistrer les modifications ?"
					onClose={busy ? () => {} : closeSaveModal}
					showCloseButton={!busy}
					buttons={[
						{
							title: "Non",
							styleName: "secondary",
							onClick: closeSaveModal,
							disabled: busy,
						},
						{
							title: postState.saving ? (
								<FontAwesomeIcon icon={faSync} spin />
							) : postState.saved ? (
								<FontAwesomeIcon icon={faCheck} />
							) : (
								"Oui"
							),
							styleName: "primary",
							onClick: save,
							disabled: busy,
						},
					]}
				>
					<p
						style={{
							margin: 0,
							fontSize: 13,
							color: COLORS.textMuted,
						}}
					>
						Voulez-vous enregistrer les modifications de ce client ?
					</p>
					{postState.error && modalError(postState.error)}
				</CustomModal>
			)}

			{showDeleteModal && (
				<CustomModal
					title="Supprimer le client ?"
					onClose={busy ? () => {} : closeDeleteModal}
					showCloseButton={!busy}
					buttons={[
						{
							title: "Non",
							styleName: "secondary",
							onClick: closeDeleteModal,
							disabled: busy,
						},
						{
							title: deleteState.deleting ? (
								<FontAwesomeIcon icon={faSync} spin />
							) : (
								"Supprimer"
							),
							styleName: "danger",
							onClick: confirmDeleteClient,
							disabled: busy,
						},
					]}
				>
					<p
						style={{
							margin: 0,
							fontSize: 13,
							color: COLORS.textMuted,
						}}
					>
						Le client « {displayName} » sera définitivement
						supprimé.
					</p>
					{deleteState.error && modalError(deleteState.error)}
				</CustomModal>
			)}

			{adresseToDelete !== null && (
				<CustomModal
					title="Supprimer l'adresse ?"
					onClose={
						deletingAdresse
							? () => {}
							: () => setAdresseToDelete(null)
					}
					showCloseButton={!deletingAdresse}
					buttons={[
						{
							title: "Annuler",
							styleName: "secondary",
							onClick: () => setAdresseToDelete(null),
							disabled: deletingAdresse,
						},
						{
							title: deletingAdresse ? (
								<FontAwesomeIcon icon={faSync} spin />
							) : (
								"Supprimer"
							),
							styleName: "danger",
							onClick: confirmRemoveAdresse,
							disabled: deletingAdresse,
						},
					]}
				>
					<p
						style={{
							margin: 0,
							fontSize: 13,
							color: COLORS.textMuted,
						}}
					>
						{(() => {
							const adr = entite.adresses[adresseToDelete]
							const label = adr
								? [adr.rue, adr.code_postal, adr.localite]
										.filter(Boolean)
										.join(" ")
								: ""
							return label
								? `L'adresse « ${label} » sera supprimée.`
								: "Cette adresse sera supprimée."
						})()}
					</p>
				</CustomModal>
			)}
			{showRdvModal && (
				<ClientToRdvModalV2
					entite={entite}
					onClose={() => setShowRdvModal(false)}
				/>
			)}
			{showAtelierModal && (
				<ClientToAtelierModalV2
					entite={entite}
					onClose={() => setShowAtelierModal(false)}
				/>
			)}
			{showLocationModal && (
				<ClientToLocationModalV2
					entite={entite}
					onClose={() => setShowLocationModal(false)}
				/>
			)}
			{QuitGuard}
		</div>
	)
}

export default ClientFicheV2
